import { HEALTH_STYLE, STATUS_STYLE } from "@/lib/format";
import { cn } from "@/lib/utils";

export const StatusChip = ({ value, label, className, testId }) => {
  const style = HEALTH_STYLE[value] || STATUS_STYLE[value] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span data-testid={testId || `chip-${String(value).toLowerCase().replace(/\s+/g, "-")}`}
      className={cn("inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md border tracking-wide uppercase whitespace-nowrap", style, className)}>
      {label ?? value}
    </span>
  );
};

export const ScoreBadge = ({ score, testId }) => {
  const tone = score >= 80 ? "bg-rose-600 text-white" : score >= 60 ? "bg-amber-500 text-white" : score >= 40 ? "bg-sky-500 text-white" : "bg-slate-300 text-slate-800 dark:bg-slate-700 dark:text-slate-100";
  return (
    <span data-testid={testId} className={cn("inline-flex items-center justify-center min-w-[2.5rem] h-6 px-2 rounded-md font-mono text-xs font-semibold", tone)}>
      {score}
    </span>
  );
};

export const Kpi = ({ label, value, sub, delta, testId, onClick, accent }) => (
  <div data-testid={testId} onClick={onClick}
    className={cn("card-surface p-5 flex flex-col gap-1.5 fade-up", onClick && "cursor-pointer hover:border-slate-400 dark:hover:border-slate-600 transition-colors")}>
    <div className="flex items-center justify-between">
      <span className="eyebrow">{label}</span>
      {accent && <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />}
    </div>
    <div className="font-heading text-2xl lg:text-[28px] font-bold tracking-tight num">{value}</div>
    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
      {delta !== undefined && delta !== null && (
        <span className={cn("font-mono font-medium", delta >= 0 ? "text-emerald-600" : "text-rose-600")}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%</span>
      )}
      {sub && <span>{sub}</span>}
    </div>
  </div>
);

export const PageHeader = ({ title, subtitle, children, testId }) => (
  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6" data-testid={testId}>
    <div>
      <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const Card = ({ title, action, children, className, testId, note }) => (
  <section data-testid={testId} className={cn("card-surface p-5 fade-up", className)}>
    {(title || action) && (
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          {title && <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{title}</h3>}
          {note && <p className="text-xs text-slate-500 mt-0.5">{note}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const Empty = ({ text = "No records", testId }) => (
  <div data-testid={testId} className="py-10 text-center text-sm text-slate-500">{text}</div>
);

export const Loader = ({ testId = "loader" }) => (
  <div data-testid={testId} className="py-16 flex items-center justify-center gap-3 text-sm text-slate-500">
    <span className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-slate-800 dark:border-t-white animate-spin" /> Computing…
  </div>
);

export const Pager = ({ page, size, total, onChange, testId = "pager" }) => {
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <div className="flex items-center justify-between pt-3 text-xs text-slate-500" data-testid={testId}>
      <span>{total.toLocaleString("en-IN")} records · page {page} of {pages}</span>
      <div className="flex gap-1">
        <button data-testid={`${testId}-prev`} disabled={page <= 1} onClick={() => onChange(page - 1)} className="px-2.5 py-1 rounded border disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800">Prev</button>
        <button data-testid={`${testId}-next`} disabled={page >= pages} onClick={() => onChange(page + 1)} className="px-2.5 py-1 rounded border disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800">Next</button>
      </div>
    </div>
  );
};

export const Table = ({ columns, rows, onRow, rowKey, testId = "table", maxH = "max-h-[560px]" }) => (
  <div className={cn("overflow-auto scrollbar-thin rounded-md border border-slate-200 dark:border-slate-800", maxH)} data-testid={testId}>
    <table className="w-full border-collapse text-left">
      <thead><tr>{columns.map((c) => <th key={c.key} className={cn("th", c.align === "right" && "text-right")}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={columns.length} className="td text-center text-slate-500 py-8">No records</td></tr>}
        {rows.map((r, i) => (
          <tr key={rowKey ? rowKey(r) : i} data-testid={`${testId}-row-${i}`} onClick={onRow ? () => onRow(r) : undefined} className={cn(onRow && "row-hover")}>
            {columns.map((c) => <td key={c.key} className={cn("td", c.align === "right" && "text-right num", c.className)}>{c.render ? c.render(r) : r[c.key]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Select = ({ value, onChange, options, placeholder, testId, className }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
    className={cn("h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40", className)}>
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
  </select>
);

export const Btn = ({ children, variant = "primary", className, testId, ...props }) => {
  const v = {
    primary: "bg-slate-900 text-white hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200",
    outline: "border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800",
    success: "bg-emerald-600 text-white hover:bg-emerald-500",
    danger: "bg-rose-600 text-white hover:bg-rose-500",
    warn: "bg-amber-500 text-white hover:bg-amber-400",
    ghost: "hover:bg-slate-100 dark:hover:bg-slate-800",
  }[variant];
  return <button data-testid={testId} className={cn("inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-sm font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed", v, className)} {...props}>{children}</button>;
};

export const heatColor = (v, max, hue = "blue") => {
  if (v === null || v === undefined || max <= 0) return "transparent";
  const t = Math.min(Math.max(v / max, 0), 1);
  const h = { blue: "217 91% 60%", green: "160 84% 39%", amber: "38 92% 50%", rose: "350 89% 60%" }[hue];
  return `hsl(${h} / ${0.08 + t * 0.72})`;
};
